import React, { useEffect, useMemo, useState } from 'react';
import axios from '../config/api';
import type { OperationOrder } from './OrderOperationsPanel';
import { sortNewestFirst } from '../utils/newestFirst';
import {
  FaChartLine,
  FaStar,
  FaThumbsDown,
  FaThumbsUp,
  FaUsers,
} from 'react-icons/fa';

export type CustomerSectionId =
  | 'retention'
  | 'feedback-rating'
  | 'feedback-menu';

export const CUSTOMER_NAV_ITEMS: { id: CustomerSectionId; label: string }[] = [
  {
    id: 'retention',
    label: 'Customer retention & new customers',
  },
  {
    id: 'feedback-rating',
    label: 'Overall store rating (90 days)',
  },
  {
    id: 'feedback-menu',
    label: 'Menu item ratings',
  },
];

interface CafeLite {
  id: string;
  name: string;
}

interface ReviewRow {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  user_name?: string;
}

interface MenuItemStatRow {
  menu_item_id: string;
  menu_item_name: string;
  avg_rating: number | null;
  rating_count: number;
}

type OrderRow = OperationOrder & { user_id?: string };

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function customerKey(o: OrderRow): string {
  return o.user_id || o.user_email || o.user_name || o.id;
}

interface CustomerInsightsPanelProps {
  orders: OrderRow[];
  cafes: CafeLite[];
  selectedCafe: string;
  activeSection?: CustomerSectionId;
}

export default function CustomerInsightsPanel({
  orders,
  cafes,
  selectedCafe,
  activeSection = 'retention',
}: CustomerInsightsPanelProps) {
  const [reviews, setReviews] = useState<ReviewRow[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [itemStats, setItemStats] = useState<MenuItemStatRow[]>([]);

  const scopedCompleted = useMemo(() => {
    let list = orders.filter((o) => o.status === 'completed');
    if (selectedCafe) {
      list = list.filter((o) => o.cafe_id === selectedCafe);
    }
    return list;
  }, [orders, selectedCafe]);

  /** Internal first-order/retention math only — not a displayed table. Oldest first. */
  const ordersByCustomerSorted = useMemo(() => {
    const map = new Map<string, OrderRow[]>();
    for (const o of scopedCompleted) {
      const k = customerKey(o);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(o);
    }
    map.forEach((arr: OrderRow[]) => {
      arr.sort((a: OrderRow, b: OrderRow) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    });
    return map;
  }, [scopedCompleted]);

  const retentionBuckets = useMemo(() => {
    const b = [0, 0, 0, 0, 0];
    ordersByCustomerSorted.forEach((arr: OrderRow[]) => {
      arr.forEach((_o: OrderRow, idx: number) => {
        const n = idx + 1;
        if (n === 1) b[0] += 1;
        else if (n === 2) b[1] += 1;
        else if (n === 3) b[2] += 1;
        else if (n === 4) b[3] += 1;
        else b[4] += 1;
      });
    });
    return { values: b };
  }, [ordersByCustomerSorted]);

  const ninetyDaysAgo = useMemo(() => addDays(startOfDay(new Date()), -90), []);

  const newCustomers90d = useMemo(() => {
    let n = 0;
    ordersByCustomerSorted.forEach((arr: OrderRow[]) => {
      if (arr.length === 0) return;
      const first = arr[0];
      if (new Date(first.created_at).getTime() >= ninetyDaysAgo.getTime()) {
        n += 1;
      }
    });
    return n;
  }, [ordersByCustomerSorted, ninetyDaysAgo]);

  const returningCustomers = useMemo(() => {
    let c = 0;
    ordersByCustomerSorted.forEach((arr: OrderRow[]) => {
      if (arr.length >= 2) c += 1;
    });
    return c;
  }, [ordersByCustomerSorted]);

  const totalCustomers = ordersByCustomerSorted.size;

  const maxRetentionBar = useMemo(
    () => Math.max(...retentionBuckets.values, 1),
    [retentionBuckets.values]
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setReviewsLoading(true);
      try {
        if (selectedCafe) {
          const [reviewsRes, statsRes] = await Promise.all([
            axios.get<ReviewRow[]>(`/api/reviews/cafe/${selectedCafe}`),
            axios.get<MenuItemStatRow[]>(`/api/reviews/cafe/${selectedCafe}/item-stats`),
          ]);
          if (!cancelled) {
            setReviews(sortNewestFirst(reviewsRes.data || [], (review) => review.created_at));
            setItemStats(statsRes.data || []);
          }
        } else if (cafes.length > 0) {
          const results = await Promise.all(
            cafes.map((c) =>
              axios.get<ReviewRow[]>(`/api/reviews/cafe/${c.id}`).then((r) => r.data || [])
            )
          );
          if (!cancelled) {
            setReviews(sortNewestFirst(results.flat(), (review) => review.created_at));
            setItemStats([]);
          }
        } else {
          if (!cancelled) {
            setReviews([]);
            setItemStats([]);
          }
        }
      } catch {
        if (!cancelled) {
          setReviews([]);
          setItemStats([]);
        }
      } finally {
        if (!cancelled) setReviewsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedCafe, cafes]);

  const reviews90d = useMemo(
    () =>
      sortNewestFirst(
        reviews.filter((r) => new Date(r.created_at).getTime() >= ninetyDaysAgo.getTime()),
        (review) => review.created_at
      ),
    [reviews, ninetyDaysAgo]
  );

  const avgRating90d =
    reviews90d.length > 0
      ? reviews90d.reduce((s, r) => s + r.rating, 0) / reviews90d.length
      : null;

  return (
    <div className="space-y-8">
      {activeSection === 'retention' && (
        <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
          <h2 className="text-xl font-bold text-amber-900 flex items-center gap-2 mb-2">
            <FaUsers className="text-amber-700" />
            Customer retention & new customers
          </h2>
          <p className="text-sm text-gray-600 mb-6 max-w-3xl">
            The Customer Insights view summarizes new customers and repeat behavior from your
            order history. Use the retention graph to see how many completed orders were a
            customer&apos;s 1st, 2nd, 3rd, or 4th (or later) with you—helping you track loyalty
            and spot opportunities to encourage repeat visits.
          </p>

          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100">
              <div className="text-xs font-semibold text-emerald-800 uppercase">New customers</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{newCustomers90d}</div>
              <p className="text-xs text-gray-600 mt-2">First order in the last 90 days</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
              <div className="text-xs font-semibold text-amber-800 uppercase">Returning customers</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{returningCustomers}</div>
              <p className="text-xs text-gray-600 mt-2">2+ completed orders (scoped)</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="text-xs font-semibold text-slate-700 uppercase">Total customers</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{totalCustomers}</div>
              <p className="text-xs text-gray-600 mt-2">With at least one completed order</p>
            </div>
          </div>

          <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
            <FaChartLine className="text-amber-600" />
            Retention graph — orders by customer order number
          </h3>
          <p className="text-xs text-gray-500 mb-3">
            Each bar counts completed orders that were that customer&apos;s Nth order at your
            selected scope (all cafés or one location).
          </p>
          <div className="flex items-end gap-2 h-44 border-b border-gray-200 pb-2 px-1">
            {retentionBuckets.values.map((v, i) => (
              <div
                key={i}
                className="flex flex-col items-center flex-1 min-w-0"
                title={`${(['1st', '2nd', '3rd', '4th', '5th+'] as const)[i]}: ${v}`}
              >
                <div
                  className="w-full max-w-[56px] bg-amber-600 rounded-t hover:bg-amber-700 transition-colors"
                  style={{ height: `${(v / maxRetentionBar) * 100}%`, minHeight: v > 0 ? 6 : 0 }}
                />
                <span className="text-[10px] text-gray-600 mt-2 text-center leading-tight">
                  {(['1st', '2nd', '3rd', '4th', '5th+'] as const)[i]}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {activeSection === 'feedback-rating' && (
        <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
          <h2 className="text-xl font-bold text-amber-900 flex items-center gap-2 mb-2">
            <FaStar className="text-amber-500" />
            Overall store rating
          </h2>
          <p className="text-sm text-gray-600 mb-4 max-w-3xl">
            From the Customer <span className="font-semibold">Feedback</span> area: your
            average star rating from the <span className="font-semibold">last 90 days</span>,
            scoped to the café filter above.
          </p>
          {reviewsLoading ? (
            <p className="text-sm text-gray-500">Loading reviews…</p>
          ) : reviews90d.length === 0 ? (
            <p className="text-sm text-gray-500">No reviews in the last 90 days for this scope.</p>
          ) : (
            <div className="flex items-baseline gap-3">
              <span className="text-4xl font-bold text-gray-900">
                {avgRating90d != null ? avgRating90d.toFixed(2) : '—'}
              </span>
              <span className="text-amber-500 text-xl">★★★★★</span>
              <span className="text-sm text-gray-500">
                from {reviews90d.length} review{reviews90d.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </section>
      )}

      {activeSection === 'feedback-menu' && (
        <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
          <h2 className="text-xl font-bold text-amber-900 flex items-center gap-2 mb-2">
            <FaThumbsUp className="text-emerald-600" />
            Menu item ratings
          </h2>
          <p className="text-sm text-gray-600 mb-4 max-w-3xl">
            Averages from optional per-item stars customers leave when they rate an order. Scope
            matches the café filter above; choose a single café to load item breakdown.
          </p>
          {!selectedCafe ? (
            <p className="text-sm text-gray-500">Select a café to see per-item ratings.</p>
          ) : reviewsLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : itemStats.length === 0 ? (
            <p className="text-sm text-gray-500">
              No per-item ratings yet. Customers can rate individual line items when they submit
              order feedback.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg max-h-72 overflow-y-auto">
              {itemStats.map((row) => (
                <li key={row.menu_item_id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                  <span className="font-medium text-amber-900 truncate">{row.menu_item_name}</span>
                  <span className="text-amber-500 shrink-0">
                    {row.avg_rating != null ? row.avg_rating.toFixed(2) : '—'} ★
                    <span className="text-gray-500 text-xs ml-2">({row.rating_count})</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-3 text-sm text-gray-600 mt-4">
            <span className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-100">
              <FaThumbsUp className="text-emerald-600" /> Higher averages suggest strong items
            </span>
            <span className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-orange-50 border border-orange-100">
              <FaThumbsDown className="text-orange-600" /> Low scores may warrant recipe or service
              checks
            </span>
          </div>
        </section>
      )}
    </div>
  );
}
